import { useAuthContext } from '@/context/auth-provider';
import { toast } from '@/hooks/use-toast';
import { userEmailChangeRequestMutationFn } from '@/lib/api';
import { useZodForm } from '@/lib/zod';
import { PrimaryEmailSchema } from '@/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, LoaderCircleIcon } from 'lucide-react';
import React, { lazy, Suspense, useCallback, useState } from 'react';
import { FormSuccess } from '../form-success';
import FormattedMessage from '../formatted-message';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { PasswordInput } from '../ui/input-password';
import { Label } from '../ui/label';

type Props = {
    openConfirmModel: boolean;
    openEmailChangeRequests: boolean;
    setOpenConfirmModel: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenEmailChangeRequests: React.Dispatch<React.SetStateAction<boolean>>;
};

const LazyEmailChangeRequest = lazy(() => import('./email-change-requests'));
export default function PrimaryEmailCard({
    openConfirmModel,
    setOpenConfirmModel,
    openEmailChangeRequests,
    setOpenEmailChangeRequests,
}: Props) {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [responseSuccess, setResponseSuccess] = useState('');
    const [isVisible, setIsVisible] = useState(false);

    const { user } = useAuthContext();
    const queryClient = useQueryClient();
    const primaryEmailForm = useZodForm(PrimaryEmailSchema, {
        defaultValues: {
            primaryEmail: user.email,
        },
        mode: 'onChange',
    });

    const { mutate, isPending } = useMutation({
        mutationFn: userEmailChangeRequestMutationFn,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['authUser'],
                refetchType: 'all',
            });
            setTimeout(() => {
                setIsVisible(false);
            }, 10000);
            setTimeout(() => {
                setResponseSuccess('');
            }, 10300);
        },
    });

    const handlePasswordSubmit = async (
        e: React.FormEvent<HTMLFormElement>,
    ) => {
        e.preventDefault();
        setIsVisible(true);
        mutate(
            {
                newEmail: primaryEmailForm.getValues('primaryEmail'),
                userId: user.id,
                password,
            },
            {
                onSuccess: (response) => {
                    handleClose();
                    setResponseSuccess(response.data.message);
                    toast({
                        title: 'Email Change Request',
                        description: (
                            <FormattedMessage
                                message={response.data.message}
                                classes='text-sm'
                            />
                        ),
                    });
                },
                onError: (error) => {
                    setError(
                        error.message || 'Failed to send email change request.',
                    );
                },
            },
        );
    };

    const handleClose = useCallback(() => {
        setPassword('');
        setError('');
        setOpenConfirmModel(false);
    }, [setOpenConfirmModel]);

    const handleOpenChange = (newOpenState: boolean) => {
        if (!newOpenState) {
            handleClose();
        }
        setOpenConfirmModel(newOpenState);
    };

    return (
        <Card className='w-full border-none bg-accent/50'>
            <CardContent className='space-y-4 p-2 md:p-6'>
                <Form {...primaryEmailForm}>
                    <form onSubmit={primaryEmailForm.handleSubmit(() => {})}>
                        <FormField
                            control={primaryEmailForm.control}
                            name='primaryEmail'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Primary Email</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            placeholder='user@example.com'
                                            disabled={isPending}
                                        />
                                    </FormControl>
                                    <FormDescription>
                                        Enter the email you want to change to
                                        and send request.
                                    </FormDescription>
                                    <FormMessage />

                                    <FormSuccess
                                        message={responseSuccess}
                                        classes={`my-4 whitespace-pre-wrap transition-opacity duration-300 ${isVisible ? 'opacity-1' : 'opacity-0'}`}
                                    />
                                </FormItem>
                            )}
                        />

                        <div className='flex flex-col md:flex-row items-center md:gap-4'>
                            <Button
                                className='my-4 w-full sm:w-auto'
                                onClick={() => {
                                    setOpenConfirmModel(true);
                                }}
                                disabled={
                                    isPending ||
                                    primaryEmailForm.control.getFieldState(
                                        'primaryEmail',
                                    ).invalid ||
                                    primaryEmailForm
                                        .watch()
                                        .primaryEmail.trim() === user.email ||
                                    (!primaryEmailForm.watch().primaryEmail &&
                                        !user.email)
                                }>
                                {isPending ? (
                                    <>
                                        <LoaderCircleIcon
                                            size={5}
                                            className='animate-spin'
                                        />
                                        Sending...
                                    </>
                                ) : (
                                    'Send Request'
                                )}
                            </Button>
                            <Button
                                className='my-4 w-full sm:w-auto'
                                onClick={() => {
                                    setOpenEmailChangeRequests(true);
                                }}
                                disabled={isPending}>
                                {isPending ? (
                                    <>
                                        <LoaderCircleIcon
                                            size={5}
                                            className='animate-spin'
                                        />
                                        Loading...
                                    </>
                                ) : (
                                    'Requests Details'
                                )}
                            </Button>
                        </div>

                        <Dialog
                            open={openConfirmModel}
                            onOpenChange={handleOpenChange}>
                            <DialogContent
                                className={`!gap-0 max-w-xl`}
                                aria-describedby='Backup Codes'>
                                <DialogHeader>
                                    <DialogTitle className='text-2xl text-slate-12 font-semibold'>
                                        Enter Password
                                    </DialogTitle>
                                </DialogHeader>
                                <DialogDescription className='mb-6'>
                                    Please enter your password to send email
                                    change request.
                                </DialogDescription>
                                <form
                                    onSubmit={handlePasswordSubmit}
                                    className='space-y-4'>
                                    <div className='space-y-2'>
                                        <Label htmlFor='password'>
                                            Password
                                        </Label>
                                        <PasswordInput
                                            id='password'
                                            placeholder='Enter your password'
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                            disabled={isPending}
                                            autoComplete='current-password'
                                        />
                                    </div>

                                    {error && (
                                        <Alert
                                            variant='destructive'
                                            className='h-fit py-4 flex items-center [&>svg]:sticky'>
                                            <AlertCircle className='size-7' />
                                            <div className='flex flex-col'>
                                                <AlertTitle className='font-bold'>
                                                    Error
                                                </AlertTitle>
                                                <AlertDescription className='whitespace-pre-wrap !translate-y-0'>
                                                    {error}
                                                </AlertDescription>
                                            </div>
                                        </Alert>
                                    )}

                                    <div className='flex justify-end space-x-2'>
                                        <Button
                                            type='button'
                                            variant='outline'
                                            onClick={handleClose}
                                            disabled={isPending}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type='submit'
                                            disabled={isPending}>
                                            {isPending && (
                                                <Loader2 className='mr-2 size-4 animate-spin' />
                                            )}
                                            {isPending
                                                ? 'Verifying...'
                                                : 'Continue'}
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </form>
                </Form>
            </CardContent>
            <Suspense fallback={''}>
                {openEmailChangeRequests && (
                    <LazyEmailChangeRequest
                        openEmailChangeRequestsDialog={openEmailChangeRequests}
                        setEmailChangeRequestsDialog={
                            setOpenEmailChangeRequests
                        }
                    />
                )}
            </Suspense>
        </Card>
    );
}
